// Permission codes (`module.action`) and default system roles — PRD §3.16, ADR-0012.
export const PERMISSIONS = [
    // identity-access
    'auth.reauth_pin',
    'auth.mfa.manage_own',
    'admin.users.manage',
    'admin.roles.manage',
    'admin.devices.manage',
    'admin.sessions.manage',
    // local admin / system
    'admin.local.backup.read',
    'admin.local.backup.run',
    'admin.local.sync.read',
    'admin.local.update.read',
    'admin.local.update.apply',
    'admin.local.license.manage',
    'admin.local.support.approve', // 09 §9: approve/reject/end a Syrova support grant (PIN)
    'admin.local.retention.manage', // 09 §10.3: retention settings + run the job now
    'system.health.read',
    'audit_log.read',
    // property setup (P2)
    'property.read',
    'property.settings.manage',
    'property.rooms.manage',
    'property.room_types.manage',
    'property.rates.manage',
    'property.taxes.manage',
    'property.charge_codes.manage',
    'property.exchange_rates.manage',
    'data.import',
    // guests (P2)
    'guest.read',
    'guest.create',
    'guest.update',
    'guest.merge',
    'guest.anonymize', // 09 §10.4: delete = anonymise (PIN)
    'guest.blacklist.manage',
    'guest.identity.read',
    'guest.identity.write',
    // reservations / front desk (P2–P3)
    'reservation.read',
    'reservation.create',
    'reservation.update',
    'reservation.cancel',
    'reservation.reinstate',
    'reservation.no_show',
    'reservation.overbook',
    'reservation.override_capacity',
    'rate.override',
    'assignment.manage',
    'checkin.perform',
    'checkin.dirty_room',
    'checkin.identity_pending',
    'checkin.before_arrival_date',
    'checkin.undo',
    'room_move.perform',
    'stay.extend',
    'stay.shorten',
    'room_status.housekeeping.set',
    'room_status.service.set',
    // billing / cashier / night audit (P4, 02b)
    'folio.view',
    'folio.create_window',
    'folio.post_charge',
    'folio.post_charge.override_price',
    'folio.reverse_line',
    'folio.transfer_line',
    'folio.close',
    'folio.post_payment',
    'folio.reverse_payment',
    'folio.refund.request',
    'folio.refund.approve',
    'folio.refund.approve_high',
    'folio.discount.post',
    'folio.discount.approve',
    'approval.view_pending',
    'invoice.issue_proforma',
    'invoice.issue_final',
    'invoice.view',
    'invoice.series.manage',
    'credit_note.issue',
    'credit_note.approve',
    'cashier_shift.open',
    'cashier_shift.close',
    'cashier_shift.reopen',
    'cashier_shift.force_close',
    'cashier_shift.view_expected',
    'cashier_shift.view_all',
    'night_audit.run',
    'night_audit.override',
    'night_audit.view',
    'business_date.rollover_admin',
    'checkout.perform',
    'checkout.reopen',
    'report.daily_pack.view',
    'report.revenue.view',
    'audit.finance.view',
    // operations (P5): housekeeping, maintenance, handover, dashboard, audit viewer
    'housekeeping.board.read',
    'housekeeping.task.manage',
    'housekeeping.task.execute',
    'room_status.housekeeping.inspect',
    'housekeeping.discrepancy.submit',
    'maintenance.ticket.create',
    'maintenance.ticket.manage',
    'shift_handover.read',
    'shift_handover.write',
    'shift_handover.close',
    'shift_handover.read_all_departments',
    'dashboard.read',
    'audit_log.export',
    // v1.1 (Phase 12): AR / city ledger (02b §16), group blocks (02 §3.5), forecasting (02 §3.21)
    'ar.account.manage',
    'ar.post_payment',
    'ar.statement.view',
    'ar.credit_limit.override',
    'folio.transfer_to_ar',
    'group.manage',
    'group.read',
    'group.checkin_bulk',
    'forecast.read',
    // channel layer (ADR-0022): accept/reject channel requests; delegation settings + reclaim
    'channel.request.decide',
    'channel.manage',
];
export function isPermission(code) {
    return PERMISSIONS.includes(code);
}
const FRONT_DESK = [
    'auth.reauth_pin',
    'property.read',
    'guest.read',
    'guest.create',
    'guest.update',
    'guest.identity.read',
    'guest.identity.write',
    'reservation.read',
    'reservation.create',
    'reservation.update',
    'reservation.cancel',
    'assignment.manage',
    'checkin.perform',
    'room_move.perform',
    'stay.extend',
    'stay.shorten',
    'checkout.perform',
    'folio.view',
    'folio.create_window',
    'folio.post_charge',
    'folio.post_payment',
    'folio.transfer_line',
    'folio.refund.request',
    'folio.discount.post',
    'invoice.issue_proforma',
    'invoice.issue_final',
    'invoice.view',
    'cashier_shift.open',
    'cashier_shift.close',
    'approval.view_pending',
    'dashboard.read',
    'housekeeping.board.read',
    'maintenance.ticket.create',
    'shift_handover.read',
    'shift_handover.write',
    'shift_handover.close',
    'group.read',
    'channel.request.decide',
];
/** System roles seeded on every node (copyable per property). Codes are stable identifiers. */
export const SYSTEM_ROLES = [
    {
        code: 'property_admin',
        nameAr: 'مدير النظام',
        nameEn: 'Property admin',
        permissions: [...PERMISSIONS],
    },
    {
        code: 'gm',
        nameAr: 'المدير العام',
        nameEn: 'General manager',
        permissions: [
            'auth.reauth_pin',
            'auth.mfa.manage_own',
            'admin.local.support.approve',
            'admin.local.retention.manage',
            'guest.anonymize',
            'audit_log.read',
            'admin.local.backup.read',
            'admin.local.backup.run',
            'admin.local.sync.read',
            'admin.local.update.read',
            'admin.local.license.manage',
            'system.health.read',
            'property.read',
            'guest.read',
            'reservation.read',
            'reservation.overbook',
            'reservation.override_capacity',
            'reservation.reinstate',
            'rate.override',
            'room_status.service.set',
            'folio.view',
            'invoice.view',
            'folio.refund.approve',
            'folio.refund.approve_high',
            'folio.discount.approve',
            'cashier_shift.view_all',
            'night_audit.view',
            'night_audit.override',
            'report.daily_pack.view',
            'report.revenue.view',
            'audit.finance.view',
            'dashboard.read',
            'housekeeping.board.read',
            'shift_handover.read',
            'shift_handover.read_all_departments',
            'audit_log.export',
            'ar.statement.view',
            'ar.credit_limit.override',
            'group.read',
            'forecast.read',
            'channel.manage',
            'channel.request.decide',
        ],
    },
    {
        code: 'fo_manager',
        nameAr: 'مدير الاستقبال',
        nameEn: 'Front office manager',
        permissions: [
            ...FRONT_DESK,
            'group.manage',
            'group.checkin_bulk',
            'channel.manage',
            'folio.transfer_to_ar',
            'ar.statement.view',
            'forecast.read',
            'audit_log.read',
            'guest.merge',
            'guest.blacklist.manage',
            'reservation.reinstate',
            'reservation.no_show',
            'reservation.overbook',
            'reservation.override_capacity',
            'rate.override',
            'property.rates.manage',
            'checkin.dirty_room',
            'checkin.identity_pending',
            'checkin.before_arrival_date',
            'checkin.undo',
            'room_status.housekeeping.set',
            'room_status.service.set',
            'data.import',
            'checkout.reopen',
            'folio.post_charge.override_price',
            'folio.reverse_line',
            'folio.close',
            'folio.reverse_payment',
            'folio.refund.approve',
            'folio.refund.approve_high',
            'folio.discount.approve',
            'credit_note.issue',
            'credit_note.approve',
            'cashier_shift.reopen',
            'cashier_shift.force_close',
            'cashier_shift.view_expected',
            'cashier_shift.view_all',
            'night_audit.run',
            'night_audit.override',
            'night_audit.view',
            'business_date.rollover_admin',
            'report.daily_pack.view',
            'report.revenue.view',
            'audit.finance.view',
            'housekeeping.task.manage',
            'housekeeping.task.execute',
            'room_status.housekeeping.inspect',
            'housekeeping.discrepancy.submit',
            'maintenance.ticket.manage',
            'shift_handover.read_all_departments',
            'audit_log.export',
        ],
    },
    { code: 'receptionist', nameAr: 'موظف استقبال', nameEn: 'Receptionist', permissions: FRONT_DESK },
    {
        code: 'night_auditor',
        nameAr: 'المدقق الليلي',
        nameEn: 'Night auditor',
        permissions: [
            ...FRONT_DESK,
            'audit_log.read',
            'reservation.no_show',
            'housekeeping.task.manage',
            'room_status.housekeeping.inspect',
            'checkout.reopen',
            'cashier_shift.view_all',
            'night_audit.run',
            'night_audit.override',
            'night_audit.view',
            'report.daily_pack.view',
            'report.revenue.view',
        ],
    },
    {
        code: 'cashier',
        nameAr: 'أمين صندوق',
        nameEn: 'Cashier',
        permissions: [
            'auth.reauth_pin',
            'property.read',
            'guest.read',
            'reservation.read',
            'folio.view',
            'folio.post_charge',
            'folio.post_payment',
            'folio.reverse_payment',
            'folio.refund.request',
            'invoice.issue_proforma',
            'invoice.issue_final',
            'invoice.view',
            'cashier_shift.open',
            'cashier_shift.close',
            'approval.view_pending',
            'dashboard.read',
            'shift_handover.read',
            'shift_handover.write',
        ],
    },
    {
        code: 'accountant',
        nameAr: 'محاسب',
        nameEn: 'Accountant',
        permissions: [
            'auth.reauth_pin',
            'audit_log.read',
            'property.read',
            'reservation.read',
            'property.exchange_rates.manage',
            'property.taxes.manage',
            'property.charge_codes.manage',
            'folio.view',
            'folio.refund.approve',
            'folio.discount.approve',
            'approval.view_pending',
            'invoice.view',
            'invoice.series.manage',
            'credit_note.issue',
            'credit_note.approve',
            'cashier_shift.view_all',
            'night_audit.view',
            'report.daily_pack.view',
            'report.revenue.view',
            'audit.finance.view',
            'dashboard.read',
            'audit_log.export',
            'shift_handover.read',
            'ar.account.manage',
            'ar.post_payment',
            'ar.statement.view',
            'folio.transfer_to_ar',
            'cashier_shift.open',
            'cashier_shift.close',
            'forecast.read',
        ],
    },
    {
        code: 'hk_supervisor',
        nameAr: 'مشرف التدبير الفندقي',
        nameEn: 'Housekeeping supervisor',
        permissions: [
            'property.read',
            'room_status.housekeeping.set',
            'room_status.service.set',
            'room_status.housekeeping.inspect',
            'housekeeping.board.read',
            'housekeeping.task.manage',
            'housekeeping.task.execute',
            'housekeeping.discrepancy.submit',
            'maintenance.ticket.create',
            'maintenance.ticket.manage',
            'shift_handover.read',
            'shift_handover.write',
            'shift_handover.close',
            'dashboard.read',
        ],
    },
    {
        code: 'housekeeper',
        nameAr: 'عامل تدبير',
        nameEn: 'Housekeeper',
        permissions: [
            'property.read',
            'room_status.housekeeping.set',
            'housekeeping.board.read',
            'housekeeping.task.execute',
            'housekeeping.discrepancy.submit',
            'maintenance.ticket.create',
            'shift_handover.read',
        ],
    },
    {
        code: 'maintenance',
        nameAr: 'صيانة',
        nameEn: 'Maintenance',
        permissions: [
            'property.read',
            'room_status.service.set',
            'housekeeping.board.read',
            'maintenance.ticket.create',
            'maintenance.ticket.manage',
            'shift_handover.read',
            'shift_handover.write',
            'shift_handover.close',
            'dashboard.read',
        ],
    },
];
//# sourceMappingURL=permissions.js.map